import {isNumber} from 'lodash-es';
import * as Yup from 'yup';

import {FeeTransactionChannels, FeeTransactionTypes} from '@/types';
import type {TransactionFee} from '@/types/AfriexFees';

export const baseFeeSchema = Yup.object()
  .shape({
    baseValue: Yup.number()
      .when('percentValue', {
        is: (percentValue: string) => isNumber(percentValue),
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.required('Fixed value required'),
      })
      .optional(),
    percentValue: Yup.number().optional(),
    type: Yup.string().required().oneOf(['default']).required(),
  })
  .noUnknown(true);

export const overrideFeeSchema = Yup.lazy((values: Partial<TransactionFee>) => {
  // Check constraint first
  const hasRequiredField =
    values?.transactionType ||
    values?.currencyPair ||
    values?.limitType ||
    values?.paymentMethod;
  if (!hasRequiredField) {
    // If constraint fails, create a schema that will always fail with our specific message
    return Yup.object().shape({
      transactionType: Yup.string().required(
        'At least one of transactionType, currencyPair, limitType, or paymentMethod is required',
      ),
    });
  }

  return Yup.object().shape({
    type: Yup.string().oneOf(['override']).required(),
    baseValue: Yup.number().when('percentValue', {
      is: (percentValue: string) => isNumber(percentValue),
      then: (schema) => schema.optional(),
      otherwise: (schema) =>
        schema.required('Fixed or Percentage value required'),
    }),
    percentValue: Yup.number().optional(),
    maxFeeValue: Yup.number().optional(),
    limit: Yup.number().when('limitType', {
      is: (limitType: string | undefined) =>
        ['above', 'below'].includes(limitType?.toLowerCase() ?? ''),
      then: (schema) =>
        schema.required().when('maxFeeValue', {
          is: (maxFeeValue: number | undefined) => maxFeeValue !== undefined,
          then: (schema) =>
            schema.max(
              Yup.ref('maxFeeValue'),
              'Limit cannot be higher than max fee value',
            ),
        }),
      otherwise: (schema) =>
        schema.optional().when('maxFeeValue', {
          is: (maxFeeValue: number | undefined) => maxFeeValue !== undefined,
          then: (schema) =>
            schema.max(
              Yup.ref('maxFeeValue'),
              'Limit cannot be higher than max fee value',
            ),
        }),
    }),
    limitType: Yup.string().oneOf(['above', 'below']).optional(),
    paymentMethod: Yup.string()
      .oneOf(Object.values(FeeTransactionChannels))
      .optional(),
    currencyPair: Yup.string().notOneOf(['*:*', '']).optional(),
    transactionType: Yup.string()
      .oneOf(Object.values(FeeTransactionTypes))
      .optional(),
  });
});
